import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable, StatusBadge, fmt } from "../components/bits";
import { FileUpload } from "../components/FileUpload";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function StudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const canWrite = ["superadmin", "admin", "secretaria"].includes(user?.role);

  const load = useCallback(() => {
    api.get(`/students/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-slate-400 text-sm">Cargando ficha…</p>;
  const s = data.student;
  const subjectName = (sid) => data.subjects.find((x) => x.id === sid)?.name || "—";

  const toggleStatus = async () => {
    try {
      await api.put(`/students/${id}`, { status: s.status === "activo" ? "retirado" : "activo" });
      toast.success(s.status === "activo" ? "Estudiante retirado (historial conservado)" : "Estudiante reactivado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="student-detail-page">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="relative group shrink-0">
              {s.photo_url ? (
                <img src={s.photo_url} alt="foto del estudiante" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" data-testid="student-360-photo" />
              ) : (
                <span className="w-16 h-16 rounded-full bg-blue-800/10 text-blue-800 flex items-center justify-center text-xl font-bold">
                  {s.first_name?.[0]}{s.last_name?.[0]}
                </span>
              )}
              {canWrite && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <FileUpload category="fotos" accept="image/*" testid="student-360-photo-upload" label=""
                    className="!p-2 rounded-full bg-white shadow-md border border-slate-200"
                    onUploaded={async (f) => {
                      try {
                        await api.put(`/students/${id}`, { photo_url: f.url });
                        toast.success("Foto actualizada");
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }} />
                </div>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="student-360-name">{s.first_name} {s.last_name}</h1>
              <p className="text-sm text-slate-500 mt-1">{s.grade} · Sección {s.section} · {s.school_year}</p>
              <div className="flex gap-2 mt-3">
                <StatusBadge value={s.status} />
                {data.balance_pending > 0 && (
                  <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid="student-balance-badge">
                    Debe {fmt(data.balance_pending)}
                  </span>
                )}
              </div>
            </div>
          </div>
          {canWrite && (
            <Button variant="outline" data-testid="toggle-student-status" onClick={toggleStatus}>
              {s.status === "activo" ? "Marcar como retirado" : "Reactivar estudiante"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          <TabsTrigger value="info" data-testid="tab-info">Información</TabsTrigger>
          <TabsTrigger value="guardians" data-testid="tab-guardians">Tutores ({data.guardians.length})</TabsTrigger>
          <TabsTrigger value="grades" data-testid="tab-grades">Calificaciones</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">Tareas</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Facturas</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Pagos</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {[["Documento", s.document], ["Nacimiento", s.birth_date], ["Sexo", s.gender === "M" ? "Masculino" : "Femenino"],
              ["Fecha de ingreso", (s.enrollment_date || "").slice(0, 10)], ["Combos", data.combos.map((c) => c.name).join(", ") || "—"],
              ["Notas", s.notes || "—"]].map(([k, v]) => (
              <div key={k}><p className="text-xs uppercase tracking-wider text-slate-400">{k}</p><p className="text-slate-800 mt-0.5">{v}</p></div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="guardians">
          <DataTable testid="guardians-table" rows={data.guardians} columns={[
            { key: "name", label: "Nombre", render: (g) => `${g.name} ${g.last_name}` },
            { key: "relationship", label: "Parentesco" },
            { key: "phone", label: "Teléfono" },
            { key: "whatsapp", label: "WhatsApp" },
            { key: "email", label: "Correo" },
            { key: "is_primary", label: "Principal", render: (g) => (g.is_primary ? "Sí" : "No") },
          ]} />
        </TabsContent>

        <TabsContent value="grades">
          <DataTable testid="grades-table" rows={data.grades} columns={[
            { key: "subject_id", label: "Materia", render: (g) => subjectName(g.subject_id) },
            { key: "evaluation", label: "Evaluación" },
            { key: "period", label: "Período" },
            { key: "score", label: "Nota", render: (g) => <span className="font-mono font-semibold">{g.score}</span> },
            { key: "published", label: "Publicada", render: (g) => (g.published ? "Sí" : "No") },
          ]} />
        </TabsContent>

        <TabsContent value="assignments">
          <DataTable testid="assignments-table" rows={data.assignments} columns={[
            { key: "title", label: "Tarea" },
            { key: "subject_id", label: "Materia", render: (a) => subjectName(a.subject_id) },
            { key: "due_date", label: "Entrega" },
            { key: "description", label: "Descripción" },
          ]} />
        </TabsContent>

        <TabsContent value="invoices">
          <DataTable testid="invoices-table" rows={data.invoices} columns={[
            { key: "number", label: "Factura", render: (i) => <span className="font-mono text-xs">{i.number}</span> },
            { key: "ncf", label: "NCF", render: (i) => i.ncf ? <span className="font-mono text-xs text-emerald-700">{i.ncf}</span> : "—" },
            { key: "issue_date", label: "Fecha" },
            { key: "total", label: "Total", render: (i) => fmt(i.total) },
            { key: "status", label: "Estado", render: (i) => <StatusBadge value={i.status} /> },
          ]} />
        </TabsContent>

        <TabsContent value="payments">
          <DataTable testid="payments-table" rows={data.payments} columns={[
            { key: "invoice_number", label: "Factura", render: (p) => <span className="font-mono text-xs">{p.invoice_number}</span> },
            { key: "amount", label: "Monto", render: (p) => fmt(p.amount) },
            { key: "method", label: "Método" },
            { key: "reference", label: "Referencia" },
            { key: "status", label: "Estado", render: (p) => <StatusBadge value={p.status} /> },
          ]} />
        </TabsContent>

        <TabsContent value="documents">
          <DataTable testid="documents-table" rows={data.documents} columns={[
            { key: "name", label: "Documento" },
            { key: "doc_type", label: "Tipo" },
            { key: "file_url", label: "Archivo", render: (d) => d.file_url ? <a className="text-sky-600 underline" href={d.file_url} target="_blank" rel="noreferrer">Ver</a> : "—" },
          ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
